/**
 * YourExposurePanel — Shows user's current portfolio exposure vs. a set of tickers
 * with stance direction (benefits_from/hurt_by).
 *
 * Props:
 *   tickers: [{ ticker, direction: 'benefits_from' | 'hurt_by', link_weight }]
 *   maxVisible: int (default 5), rest collapsed into "+ N more"
 */

import { useState, useEffect } from 'react';

export default function YourExposurePanel({ tickers = [], maxVisible = 5 }) {
  const [holdings, setHoldings] = useState(null);
  const [capital, setCapital] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [hRes, pRes] = await Promise.all([
          fetch('/api/profile/holdings'),
          fetch('/api/profile'),
        ]);
        if (hRes.ok) setHoldings(await hRes.json());
        if (pRes.ok) {
          const p = await pRes.json();
          setCapital(p?.total_investable_capital || null);
        }
      } catch {}
    }
    load();
  }, []);

  // Loading
  if (holdings === null) return <div className="text-[10px] text-korinth-muted font-mono">Loading exposure…</div>;

  // No holdings configured
  if (holdings.length === 0) {
    return (
      <div className="text-[10px] text-korinth-muted font-mono italic">
        Set up holdings in <a href="/settings" className="text-korinth-accent hover:underline">Settings → Profile</a> to see your exposure vs. this theme.
      </div>
    );
  }

  const totalValue = holdings.reduce((s, h) => s + (h.current_value || h.quantity * (h.avg_cost || 0)), 0);
  const denom = capital || totalValue || 1;

  const lines = tickers.map(tl => {
    const holding = holdings.find(h => h.ticker === tl.ticker);
    const pctOfPortfolio = holding ? ((holding.current_value || holding.quantity * (holding.avg_cost || 0)) / denom) * 100 : 0;

    if (tl.direction === 'benefits_from') {
      if (holding) {
        return { icon: '✅', text: `Holding ${tl.ticker}: ${pctOfPortfolio.toFixed(1)}% (aligned)`, color: '#10b981' };
      }
      return { icon: '⚠️', text: `No position in ${tl.ticker} (theme-recommended)`, color: '#f59e0b' };
    } else { // hurt_by
      if (holding) {
        return { icon: '🔴', text: `Holding ${tl.ticker}: ${pctOfPortfolio.toFixed(1)}% (theme says reduce)`, color: '#ef4444' };
      }
      return { icon: '✅', text: `No exposure to ${tl.ticker} (aligned)`, color: '#10b981' };
    }
  });

  // Sort: held positions first (more actionable), then unheld
  lines.sort((a, b) => {
    const aHeld = a.text.startsWith('Holding');
    const bHeld = b.text.startsWith('Holding');
    if (aHeld !== bHeld) return aHeld ? -1 : 1;
    return 0;
  });

  const visible = showAll ? lines : lines.slice(0, maxVisible);
  const hidden = lines.length - visible.length;

  return (
    <div className="space-y-0.5">
      {visible.map((l, i) => (
        <div key={i} className="flex items-start gap-1.5 text-[10px] font-mono" style={{ color: l.color }}>
          <span className="flex-shrink-0">{l.icon}</span>
          <span>{l.text}</span>
        </div>
      ))}
      {hidden > 0 && (
        <button onClick={() => setShowAll(true)} className="text-[9px] font-mono text-korinth-muted hover:text-korinth-accent">
          + {hidden} more…
        </button>
      )}
    </div>
  );
}
